import moment from 'moment'
import { DateTime } from 'luxon'
import EventBus from '@/components/mixins/event-bus-mixin'
import AuthMixin from '@/components/mixins/auth-mixin'
import SelectedScheduleItem from './selected-schedule-item/selected-schedule-item-tmpl'
import ScheduleSearchBar from './schedule-search-bar/schedule-search-bar-tmpl'
import MediaMenu from '@/components/common/media-menu/media-menu-tmpl'
import MonthlyCalendar from '@/components/common/monthly-calendar/monthly-calendar-tmpl'
import TagSlider from '@/components/common/tag-slider/tag-slider-tmpl'
import SelectedScheduleTagSlider from './selected-schedule-tags-slider/selected-schedule-tags-slider-tmpl'
import SchedulePlaytimeMenuItem from '@/components/common/media-menu/schedule-playtime-menu-item/schedule-playtime-menu-item-tmpl'
import MediaScheduleMixin from '@/components/mixins/media-schedule-mixin'
import PlaytimeForm from '@/components/common/media-menu/schedule-playtime-menu-item/playtime-form/playtime-form-tmpl'
import UnsavedDataMixin from '@/components/mixins/unsaved-data-mixin'
import Canvas from '@/components/pages/player/player-setup/player-data-canvas'

import _ from 'lodash'
import CONST from '@/globals/constants'
import { tagsStore } from '@/pinia/stores/tagsStore'
import { useGlobalStore } from '@/pinia/stores/globalStore'
import { scheduleStore } from '@/pinia/stores/schedule/scheduleStore'
import { useFileUploaderStore } from '@/pinia/stores/file-upload/fileUploaderStore'
import { mapState } from 'pinia'
import { useMixpanel } from '@base-shared/composables/use-mixpanel'
import { useCognitoAuthStore } from '@base-shared/stores/cognito-auth'

export default {
  name: 'schedule',
  inject: [
    '$currentUser',
    '$utils',
    '$dataStore',
    '$api',
    '$apiError'
  ],
  components: {
    SelectedScheduleItem,
    MediaMenu,
    MonthlyCalendar,
    ScheduleSearchBar,
    TagSlider,
    SelectedScheduleTagSlider,
    SchedulePlaytimeMenuItem,
    PlaytimeForm
  },
  computed: {
    ...mapState(tagsStore, ['tagSliderTagCombinations']),
    ...mapState(useGlobalStore, {
      playtimeFormat: 'preferredTimeFormat'
    }),
    storeOnLoadTagsFromSlider () {
      const onLoadTagSlider = this.tagSliderTagCombinations
      let res = onLoadTagSlider

      // Make sure it's a flat array
      if ((typeof onLoadTagSlider === 'object' && !Array.isArray(onLoadTagSlider) && onLoadTagSlider !== null)) {
        res = []
        for (const tagType in onLoadTagSlider) {
          for (const arrIndx in onLoadTagSlider[tagType]) {
            const tag = onLoadTagSlider[tagType][arrIndx]
            tag.type = tagType
            res.push(tag)
          }
        }
      }

      return res
    }
  },
  watch: {
    storeOnLoadTagsFromSlider: {
      handler (newVal) {
        this.tagsSliderLoaded = true
      },
      deep: true
    },
    perfectScrollbarMounted () {
      this.slideTimeSliderToCurrentHour()
    },
    playtimeFormat () {
      const newPlayTime = this.playtimeFormData
      newPlayTime._edit.title = this.createScheduleTimeTitle(newPlayTime, { timeFormat: this.playtimeFormat }).range
      this.updatedPlaytimeFormData = newPlayTime
      this.playtimeFormData = newPlayTime
    }
  },
  setup () {
    const fileUploaderStore = useFileUploaderStore();

    return { fileUploaderStore }
  },
  data () {
    return {
      CONST,
      enableEditCalendar: this.$currentUser.getRights().userSchedule.editCalendar,
      thisComponentAsReferenceToAnyChildComponents: this,
      scheduleList: {
        batchHistory: [],
        itemsToShow: [],
        nextFetch: {
          hasMore: false,
          nextToken: null
        },
        reqFilter: { search: '' }
      },
      refreshTagSlider: { ref: null, refresh: false },
      eventBarColors: ['#52f9e5', '#4bccdb', '#628bc8', '#7471c5', '#9443c2'],
      selectedDay: null,
      playerHoursList: 24,
      selectedSchedule: null,
      selectedScheduleTags: [],
      mediaMenuVisible: false,
      mediaMenuTriggerPropertyAction: {},
      showSideLoader: false,
      triggerPropertyActionResponseFromMediaMenu: null,
      showNoSearchResults: false,
      isSearchLoading: false,
      playersTags: [],
      selectedTagsFromSlider: [],
      triggerPreviousSearch: null,
      mediaEventsSelected: null,
      playtimeFormData: null,
      playtimeTags: [],
      updatedPlaytimeFormData: null,
      showPlaytimeFormSubmitBtn: false,
      updatedMediaEvents: { selectedEventRowRef: [], updatedMedia: [], playtimeFormSubmitted: false },
      playersMaxRequest: 10,
      showPlaytimeForm: false,
      tagsSliderLoaded: false,
      scheduleListLoaded: false,
      showTagExcludeIcon: { show: false, icon: '' }, // Add exclusion filter for the smart adds,
      tagExcludeIconChanged: false,
      mouseOverScheduleRow: null,
      disabledAllTag: false,
      playerSearchText: null,
      perfectScrollbarMounted: false,
      canvasRepository: [],
      notification: null,
    }
  },
  mixins: [AuthMixin, MediaScheduleMixin, UnsavedDataMixin],
  methods: {
    handleDismissNotification () {
      this.notification = null
    },
    getSelectedTags () {
      let tags = []

      // Check first form URl query params
      if (this.$route.query.tags) {
        tags = JSON.parse(this.$route.query.tags)
      }

      return tags
    },
    getSearchText () {
      let search = null

      // Check first form URl query params
      if (this.$route.query.search) {
        search = this.$route.query.search
      }

      return search
    },
    /**
     * Get Schedule list from the backend
     * @param {String} start
     * @param {String} duration
     * @param {Object} params
     */
    getScheduleList (start, duration, params = {}) {
      const apiRequest = async () => {
        this.scheduleListLoaded = false
        this.$utils.mainLoader(true)

        // Reset Schedule List
        if (params.resetList) {
          this.scheduleList = {
            batchHistory: [],
            itemsToShow: [],
            nextFetch: {
              hasMore: false,
              nextToken: null
            },
            reqFilter: { search: '' }
          }
        }

        const requestParams = {
          start,
          dur: duration,
          width: 512,
          max: this.playersMaxRequest,
          nextToken: params.nextToken
        }

        // Add tags or search text on first load
        if (params.event === 'mounted' || params.event === 'windowScroll') {
          if (this.getSelectedTags().length) {
            requestParams.tags = JSON.stringify(this.getSelectedTags())
          }

          if (this.getSearchText()) {
            requestParams.search = this.getSearchText()
          }
        }

        const store = scheduleStore()
        const result = await store.fetchExtendedSchedules(requestParams)

        if (!result.success) {
          this.$utils.mainLoader(false)
          this.$apiError.handleUnauthorizedError(result.error, { originalRequest: () => { apiRequest() } })
          if (result.error?.response?.status === CONST.UNAUTHORIZED_CODE) {
            this.scheduleListLoaded = true
            return
          }

          const errorHeaders = result.error?.response?.headers
          const requestId = errorHeaders
            ? typeof errorHeaders.get === 'function'
              ? errorHeaders.get('x-amzn-requestid')
              : errorHeaders['x-amzn-requestid'] ?? null
            : null
          const errorCode = result.error?.response?.status ?? 'N/A'

          this.notification = {
            notifId: 'schedule-error-notif',
            type: 'danger',
            icon: 'flowbite:info-circle-solid',
            htmlTitle: this.$t('errors.somethingWentWrong', { errorCode }),
            htmlMessage: this.$t('errors.somethingWentWrongDescription'),
            ...(requestId && { requestId }),
            closable: true,
          }
          this.scheduleListLoaded = true
          return
        }

        const newScheduleBatch = result.data.items

        const processNewbatch = () => {
          const scheduleList = this.$utils.clone(this.scheduleList)

          // Process newScheduleBatch
          if (newScheduleBatch.length > 0) {
            // Put the newScheduleBatch into batch history
            scheduleList.batchHistory.push(newScheduleBatch)

            // Add each item into itemsToShow
            for (const scheduleItem of newScheduleBatch) {
              const existInToShowList = scheduleList.itemsToShow.find((s) => { return s.scheduleId === scheduleItem.scheduleId })
              if (!existInToShowList) {
                scheduleList.itemsToShow.push(scheduleItem)
              }
            }

            // Track pagination state from nextToken
            scheduleList.nextFetch.hasMore = !!result.data.nextToken
            scheduleList.nextFetch.nextToken = result.data.nextToken ?? null

            this.scheduleList = this.filterScheduleList(scheduleList)
          }
        }
        processNewbatch()

        // Set Player Tags
        const playersTags = this.$utils.clone(this.playersTags)
        result.data.tags?.forEach((tag) => {
          if (playersTags.indexOf(tag) < 0) {
            playersTags.push(tag)
          }
        })
        this.playersTags = playersTags
        this.$utils.updatePageComponentStyle({ name: 'schedulePage' })

        if (params.callback) {
          params.callback()
        } else {
          this.$utils.mainLoader(false)
        }

        this.scheduleListLoaded = true
      }
      apiRequest()
    },
    filterScheduleList (filterScheduleListSrc) {
      const scheduleList = filterScheduleListSrc

      const scheduleListBackgroundOptions = ['background-cloud1', 'background-cloud2', 'background-cloud3', 'background-cloud4']
      const scheduleListBackgroundColorOptions = ['#52f9e5', '#4bccdb', '#628bc8', '#7471c5']
      const scheduleListBackgroundColorOptionsFade = ['#bafdf5', '#b7ebf1', '#c0d1e9', '#c7c6e8']
      const defaultPreviewDimension = {
        horizontalSmallScreen: {
          width: 105,
          height: 59
        },
        horizontalLargeScreen: {
          width: 180,
          height: 101
        }
      }

      scheduleList.itemsToShow.forEach((schedule, indx) => {
        schedule.dimensions = schedule.dir !== 'vertical'
          ? '1920x1080'
          : '1080x1920'

        if (!schedule._edit) {
          schedule._edit = {
            isRowMediaActionSelected: false,
            selectedMediaAction: null,
            scheduleRowHeight: this.$utils.is1280View() ? 59 : 101,
            previewCardWrapperStyle: {
              width: this.$utils.is1280View() ? `${defaultPreviewDimension.horizontalSmallScreen.width}px` : `${defaultPreviewDimension.horizontalLargeScreen.width}px`,
              height: this.$utils.is1280View() ? `${defaultPreviewDimension.horizontalSmallScreen.height}px` : `${defaultPreviewDimension.horizontalLargeScreen.height}px`
            },
            previewCardStyle: {
              width: this.$utils.is1280View() ? `${defaultPreviewDimension.horizontalSmallScreen.width}px` : `${defaultPreviewDimension.horizontalLargeScreen.width}px`,
              height: this.$utils.is1280View() ? `${defaultPreviewDimension.horizontalSmallScreen.height}px` : `${defaultPreviewDimension.horizontalLargeScreen.height}px`,
              right: '0px'
            },
            led: false,
            ledVertical: false
          }
        }

        // Add uid and iframeStatus for preview
        schedule._edit.uid = schedule.scheduleId
        schedule._edit.iFrameStatus = 'default'

        // Add background class
        const backgroundClassName = scheduleListBackgroundOptions[indx % scheduleListBackgroundOptions.length]
        const backgroundColor = scheduleListBackgroundColorOptions[indx % scheduleListBackgroundColorOptions.length]
        const backgroundColorFade = scheduleListBackgroundColorOptionsFade[indx % scheduleListBackgroundColorOptions.length]

        schedule._edit.backgroundClassName = JSON.parse(JSON.stringify(backgroundClassName))
        schedule._edit.backgroundColor = backgroundColor
        schedule._edit.backgroundColorFade = backgroundColorFade

        // Filter times
        schedule.times = this.filterScheduleTimes(schedule.times)

        // Schedule status
        if (!schedule._isInErrorStatus && !schedule._playerStatus) {
          schedule._isInErrorStatus = false
          schedule._playerStatus = 'publish'
        }

        // Update Preview Style for Regular Vertical screen
        if (schedule.dir && schedule.dir === 'vertical') {
          schedule._edit.scheduleRowHeight = this.$utils.is1280View() ? 105 : 180

          schedule._edit.previewCardWrapperStyle = {
            width: this.$utils.is1280View() ? `${defaultPreviewDimension.horizontalSmallScreen.width}px` : `${defaultPreviewDimension.horizontalLargeScreen.width}px`,
            height: this.$utils.is1280View() ? `${defaultPreviewDimension.horizontalSmallScreen.width}px` : `${defaultPreviewDimension.horizontalLargeScreen.width}px`
          }

          schedule._edit.previewCardStyle = {
            width: this.$utils.is1280View() ? `${defaultPreviewDimension.horizontalSmallScreen.height}px` : `${defaultPreviewDimension.horizontalLargeScreen.height}px`,
            height: this.$utils.is1280View() ? `${defaultPreviewDimension.horizontalSmallScreen.width}px` : `${defaultPreviewDimension.horizontalLargeScreen.width}px`,
            right: '0px'
          }
        }

        // Update Preview Style for LED screen
        const getLEDIndexInTags = schedule.tags ? schedule.tags.findIndex((tag) => { return tag.includes('LED:') }) : -1
        if (getLEDIndexInTags >= 0) {
          const parsedLedTag = this.$utils.parseLedTag(schedule.tags[getLEDIndexInTags])
          const ledWidth = parsedLedTag.width ?? 0
          const ledHeight = parsedLedTag.height ?? 0
          const isVertical = ledWidth < ledHeight
          const maxCardWidth = this.$utils.is1280View() ? 105 : 180
          const maxCardHeight = this.$utils.is1280View() ? 59 : 101
          const calculatePreviewDimension = this.$utils.calculateAspectRatioFit({
            srcWidth: ledWidth,
            srcHeight: ledHeight,
            maxWidth: isVertical ? maxCardHeight : maxCardWidth,
            maxHeight: isVertical ? maxCardWidth : maxCardHeight
          })
          schedule.dimensions = parsedLedTag.dimension

          schedule._edit.led = true
          schedule._edit.ledVertical = isVertical
          schedule.dir = 'horizontal'
          schedule._edit.scheduleRowHeight = this.$utils.is1280View() ? 59 : 101

          // Adjust Card Style
          schedule._edit.previewCardStyle = {
            width: `${calculatePreviewDimension.cardWidth}px`,
            height: `${calculatePreviewDimension.cardHeight}px`,
            left: `${(maxCardWidth - calculatePreviewDimension.cardWidth) / 2}px`, // Horizontally centered in the Preview wrapper
            top: `${Math.max((parseInt(maxCardHeight - calculatePreviewDimension.cardHeight) / 2), 0)}px`
          }

          // Adjust card wrapper style if vertical
          if (isVertical) {
            schedule.dir = 'vertical'
            schedule._edit.scheduleRowHeight = calculatePreviewDimension.cardHeight
            schedule._edit.previewCardWrapperStyle = {
              width: this.$utils.is1280View() ? `${defaultPreviewDimension.horizontalSmallScreen.width}px` : `${defaultPreviewDimension.horizontalLargeScreen.width}px`,
              height: `${calculatePreviewDimension.cardHeight}px`,
              right: '0px'
            }
          }

          /**
           * Create media preview style for LED screen
           */
          const maxLEDMediaPreviewWidth = this.$utils.is1280View() ? 75 : 130
          const maxLEDMediaPreviewHeight = this.$utils.is1280View() ? 42 : 72
          const calculateMediaPreviewDimension = this.$utils.calculateAspectRatioFit({
            srcWidth: ledWidth,
            srcHeight: ledHeight,
            maxWidth: isVertical ? maxLEDMediaPreviewHeight : maxLEDMediaPreviewWidth,
            maxHeight: isVertical ? maxLEDMediaPreviewWidth : maxLEDMediaPreviewHeight
          })

          schedule._edit.mediaPreviewCardStyle = {
            width: `${calculateMediaPreviewDimension.cardWidth}px`,
            height: `${calculateMediaPreviewDimension.cardHeight}px`,
            top: `${Math.max((parseInt(maxLEDMediaPreviewHeight - calculateMediaPreviewDimension.cardHeight) / 2), 0)}px`
          }
        }
      })

      return scheduleList
    },
    createTimeOptionsAndGetSchedules ({ event, selectedDay }) {
      let startDate = moment()
      if (selectedDay) {
        startDate = moment(selectedDay.fullDate)
      }

      // Create time options
      const hours = []
      for (let i = 0; i <= 24; i++) {
        const hour = {
          H: `${i}:00`,
          halfH: `${i}:30`
        }

        if (i < 10) {
          hour.H = `0${i}:00`
          hour.halfH = `0${i}:30`
        }

        hours.push(hour)
      }
      this.playerHoursList = hours

      // Set current date as selected by default
      this.selectedDay = {
        day: startDate.clone().format('ddd'),
        date: startDate.clone().format('DD'),
        month: startDate.clone().format('MMM'),
        monthId: startDate.clone().format('M'),
        fullDate: startDate.clone().format('YYYY-MM-DD')
      }

      // Get schedule list
      const getSchedStart = DateTime.fromISO(startDate.clone().format('YYYY-MM-DD'), { zone: 'utc' }).startOf('day').toISO()
      const getSchedDur = 24 * 3600 // 24H
      this.getScheduleList(
        getSchedStart,
        getSchedDur,
        {
          callback: () => {
            this.$nextTick(() => {
              // Auto select player if serialNumber is present from url
              const selectedPlayerSerialNumberFromURL = this.$route.query?.scheduleId ?? null
              if (selectedPlayerSerialNumberFromURL) {
                const scheduleDetails = this.scheduleList.itemsToShow.find((item) => item.scheduleId === selectedPlayerSerialNumberFromURL)

                if (scheduleDetails) {
                  this.handleScheduleItemClickCapture({}, scheduleDetails)
                } else {
                  this.$router.push({ path: '/schedule' })
                }
              }
              this.$utils.mainLoader(false)
            })
          },
          resetList: true,
          event
        })
    },
    slideTimeSliderToCurrentHour () {
      const numOfPixelsPerHour = this.$utils.is1920View() ? 142 : 93
      const leftPosition = moment().format('H') * numOfPixelsPerHour

      if (this.perfectScrollbarMounted) {
        $(this.$refs.timeSliderControl.ps.element).scrollLeft(leftPosition)
      }
    },
    /**
     * Hook function for monthly calendar component change
     * @param {Object} day
     */
    handleMonthlyCalendarChange (event) {
      if (event.action === 'DAY_SELECTION') {
        const selectedDay = event.payload.data

        if (this.selectedDay.fullDate !== selectedDay.fullDate) {
          this.selectedDay = selectedDay
          this.moveTopSliderToTriggerAutoSyncOnceDataIsUpdated()

          const getSchedStart = DateTime.fromISO(selectedDay.fullDate, { zone: 'utc' }).startOf('day').toISO()
          const getSchedDur = 24 * 3600 // 24H
          this.getScheduleList(
            getSchedStart,
            getSchedDur,
            {
              callback: () => {
                this.slideTimeSliderToCurrentHour()
                this.$utils.mainLoader(false)
              },
              resetList: true
            })
        }
      }
    },
    moveTopSliderToTriggerAutoSyncOnceDataIsUpdated () {
      if (!this.$refs.timeSliderControl) {
        return
      }

      const parentPlayerTimeSectionEl = $(this.$refs.timeSliderControl.ps.element)
      const currentLeftScroll = parentPlayerTimeSectionEl.scrollLeft()
      const leftScroll = (currentLeftScroll > 0) ? currentLeftScroll - 1 : currentLeftScroll + 1
      parentPlayerTimeSectionEl.scrollLeft(leftScroll)
    },
    handleTimeSliderScroll () {
      if (!this.$refs.timeSliderControl) {
        return
      }

      const clonedPlayerTimeSection = $(this.$refs.timeSliderControl.ps.element)
      // Synchronize all timeline horizontal scrollbar
      clonedPlayerTimeSection.scroll(() => {
        $('.time-slider-control-sync').scrollLeft(clonedPlayerTimeSection.scrollLeft())
      })
    },
    handleShowingOfAllPlayers () {
      if (this.disabledAllTag) return

      const nextAction = () => {
        this.resetPlaytimeForm()
        this.selectedSchedule = null

        // Update File Uploader config
        this.fileUploaderStore.setUploaderConfig({
          mediaListUploadIconClicked: false,
          triggerClickDropzone: false,
          playerId: null,
          targetComponent: 'schedules'
        })

        this.selectedSchedule = null

        // Update current url
        const query = {}

        if (this.getSelectedTags().length) {
          query.tags = JSON.stringify(this.getSelectedTags())
        }

        if (this.getSearchText()) {
          query.search = this.getSearchText()
        }

        this.$router.push({
          path: '/schedule',
          query
        })

        // Post Message and update page style
        this.postMessageToSVG({ type: 'all' })
        this.handleTimeSliderScroll()
        this.moveTopSliderToTriggerAutoSyncOnceDataIsUpdated()
        this.$utils.updatePageComponentStyle({ name: 'schedulePage' })
      }

      this.checkUnsavedData({
        nextAction: () => {
          nextAction()
        }
      })
    },
    handleDeleteAllScheduledMediaFromTarget (scheduleId) {
      const deleteAllScheduledMedia = () => {
        this.$utils.mainLoader(true)
        this.$api.delete(`${CONFIG.API_BASE_URL}/player/${scheduleId}/schedule`)
          .then(response => {
            this.createTimeOptionsAndGetSchedules({ action: 'scheduleDeleted' })
            this.handleTimeSliderScroll()
          })
          .catch((error) => {
            this.$utils.mainLoader(false)
            this.$apiError.handleAPIRequestError(error, { originalRequest: () => { deleteAllScheduledMedia() }, showDialogNotification: true })
          })
      }
      deleteAllScheduledMedia()
    },
    /**
     * Handle click capture event on the entire schedule item row
     * @param {Object} event - mouse click event
     * @param {Object} selectedSchedule - selected schedule item
     */
    handleScheduleItemClickCapture (event, selectedSchedule) {
      const isSetupActionBtnClicked = event.target && $(event.target).hasClass('setup-not-configured-player')
      const isPlayerPreviewClicked = event.target && $(event.target).hasClass('preview-card-wrapper-cover')
      const isMediaActionBtnDeleteClicked = event.target && $(event.target).hasClass('media-action-icon') && $(event.target).hasClass('deleteMediaSchedule')

      if (isSetupActionBtnClicked) {
        this.$router.push(`/setup/${selectedSchedule.scheduleId}/${selectedSchedule.name}`, () => {})
        return
      }

      // Show player prview
      if (isPlayerPreviewClicked && (selectedSchedule._edit && ['unconfigured', 'failed', 'disconnected'].indexOf(selectedSchedule._edit.playerStatus) < 0)) {
        EventBus.$emit('SHOW_PLAYER_PREVIEW_POPUP', {
          preview: this.$utils.clone(selectedSchedule.preview),
          previewOrientation: (selectedSchedule.dir) ? selectedSchedule.dir : 'horizontal',
          ledTag: selectedSchedule.tags ? selectedSchedule.tags.find((tag) => { return tag.includes('LED:') }) : null,
          source: 'schedulePage'
        })

        return
      }

      if (isMediaActionBtnDeleteClicked) {
        EventBus.$emit('SHOW_CUSTOM_NOTIFICATION', {
          type: 'warning',
          description: this.$t('confirm.removeTheMediaFromTheSchedule'),
          callbacks: {
            eventOKButtonClick: () => {
              this.handleDeleteAllScheduledMediaFromTarget(selectedSchedule.scheduleId)
            }
          }
        })

        return
      }

      // Some extra spaces to deny entire row on click
      if (event.target && $(event.target).hasClass('deny-row-click-capture')) {
        return
      }

      // Entire schedule item row clicked
      this.selectedSchedule = selectedSchedule
      const selectedSchedTags = selectedSchedule.tags ? selectedSchedule.tags : []
      const newSelectedSchedTags = []

      selectedSchedTags.forEach((tag) => {
        let newTag = tag

        if (typeof (tag) === 'string') {
          newTag = { value: tag }
        }

        // Get color from tags slider
        const detailsFromSlider = this.storeOnLoadTagsFromSlider.find((t) => { return t.name === newTag.value })
        if (detailsFromSlider && detailsFromSlider.color) {
          newTag.color = detailsFromSlider.color
        }

        newSelectedSchedTags.push(newTag)
      })

      this.selectedScheduleTags = newSelectedSchedTags
      this.mediaMenuVisible = false
      this.postMessageToSVG({ type: 'selectedSchedule' })
      this.removeMessageListener()
      this.$nextTick(_ => {
        this.$utils.updatePageComponentStyle({ name: 'schedulePage' })
      })

      // Router push to selected player
      if (!this.$route.query?.scheduleId) {
        // Update current url
        const query = {
          scheduleId: this.selectedSchedule.scheduleId
        }

        if (this.getSelectedTags().length) {
          query.tags = JSON.stringify(this.getSelectedTags())
        }

        if (this.getSearchText()) {
          query.search = this.getSearchText()
        }

        this.$router.push({
          path: '/schedule',
          query
        })
      }
    },
    resetPlaytimeForm () {
      this.mediaEventsSelected = null
      this.playtimeFormData = null
      this.updatedPlaytimeFormData = null
      this.showPlaytimeFormSubmitBtn = false
      this.updatedMediaEvents = { selectedEventRowRef: [], updatedMedia: [], playtimeFormSubmitted: false }
    },
    handlePlaytimeFormTagClick () {
      if (!this.showPlaytimeForm) {
        this.showPlaytimeForm = true
      } else {
        this.showPlaytimeForm = false
      }
    },
    handleScheduleItemCompChange (event) {
      if (event.action === 'RESET_SCHEDULE_ITEM_SELECTION') {
        const nextAction = () => { this.handleShowingOfAllPlayers() }

        if (this.showPlaytimeFormSubmitBtn) {
          this.handleSubmitPlaytimeForm({ nextAction })
        } else {
          nextAction()
        }
      }

      if (event.action === 'SORTING_REQUESTS_CHANGED') {
        if (event.payload.data.length) {
          this.disabledAllTag = true
        } else {
          this.disabledAllTag = false
        }
      }

      if (event.action === 'NEW_MEDIA_PLAYTIME_SELECTION') {
        const eventData = event.payload.data
        const eventRowSelection = eventData.eventRowSelection ? eventData.eventRowSelection : []

        this.showPlaytimeForm = false
        this.playtimeTags = []

        if (['resetMediaSelection', 'scheduleEventRowSelected', 'editMediaSchedule'].indexOf(event.payload.data.action) >= 0) {
          const processNewPlaytimeSelection = () => {
            this.resetPlaytimeForm()
            this.showTagExcludeIcon = { show: false, icon: '' }
            this.tagExcludeIconChanged = false

            if (eventRowSelection.length) {
              const playtimeSourceData = {
                daily: eventRowSelection[0].daily,
                duration: eventRowSelection[0].duration,
                startDate: eventRowSelection[0].startDate,
                endDate: eventRowSelection[0].endDate,
                startTime: eventRowSelection[0].startTime,
                priority: eventRowSelection[0].priority,
                repeatDays: eventRowSelection[0].repeatDays
              }

              // Single playtime selection
              if (eventRowSelection.length === 1) {
                playtimeSourceData.uid = eventRowSelection[0].uid

                // Show Add exclusion filter for adds if item scheduled to a TAG or smart-sync
                if ((eventRowSelection[0].adds)) {
                  if (eventRowSelection[0].excludes && eventRowSelection[0].excludes.indexOf(this.selectedSchedule.scheduleId) >= 0) {
                    this.showTagExcludeIcon = { show: true, icon: 'exclude' }
                  } else {
                    this.showTagExcludeIcon = { show: true, icon: 'include' }
                  }
                }
              }

              // Multiple playtime selection
              if (eventRowSelection.length > 1) {
                const isAllPlaytimeDataTheSame = eventRowSelection.every((playtime) => {
                  return JSON.stringify(playtimeSourceData) === JSON.stringify({
                    daily: playtime.daily,
                    duration: playtime.duration,
                    startDate: playtime.startDate,
                    endDate: eventRowSelection[0].endDate,
                    startTime: playtime.startTime,
                    priority: playtime.priority,
                    repeatDays: playtime.repeatDays
                  })
                })

                playtimeSourceData.uid = `multiplePlaytimeSummary_${this.$utils.generateUniqueID()}`
                playtimeSourceData.multiplePlaytimeSummary = true
                playtimeSourceData.allPlaytimeTheSame = false

                if (isAllPlaytimeDataTheSame) playtimeSourceData.allPlaytimeTheSame = true
              }

              if (!playtimeSourceData._edit) playtimeSourceData._edit = { title: '' }

              // Add title
              playtimeSourceData._edit.title = this.createScheduleTimeTitle(playtimeSourceData, { timeFormat: this.playtimeFormat }).range

              this.playtimeFormData = playtimeSourceData

              // Process playtime tags
              if (eventData.tagsSummary && eventData.tagsSummary.length) {
                this.playtimeTags = eventData.tagsSummary.map((tag) => {
                  tag.label = tag?.value ?? ''

                  // Remove prefix tag- from backend
                  if (tag.label.startsWith('tag-')) {
                    tag.label = tag.label.replace('tag-', '')
                  }

                  return tag
                })
              }
            }

            this.mediaEventsSelected = event.payload.data

            if (event.payload.data.action === 'resetMediaSelection') {
              this.resetPlaytimeForm()
            }
          }

          processNewPlaytimeSelection()
          this.$utils.mainLoader(false)
        }

        if (['playtimeOrderUpdated'].indexOf(event.payload.data.action) >= 0) {
          const mediaEventsSelected = this.$utils.clone(this.mediaEventsSelected)

          mediaEventsSelected.mediaSelection.filter((media) => {
            if (media.schedules) {
              const schedTargetIndx = media.schedules.findIndex((sched) => { return sched.target === mediaEventsSelected.scheduleId })

              for (const crntPlaytimeIndx in media.schedules[schedTargetIndx].custom) {
                const currentPlaytime = media.schedules[schedTargetIndx].custom[crntPlaytimeIndx]

                if (currentPlaytime.uid === event.payload.data.uid) {
                  media.schedules[schedTargetIndx].custom[crntPlaytimeIndx].order = event.payload.data.newOrder
                }
              }
            }

            return media
          })

          this.mediaEventsSelected = mediaEventsSelected
        }
      }

      if (event.action === 'NEW_MEDIA_PLAYTIME_SELECTION_ADDS_CHANGED' && this.mediaEventsSelected) {
        const eventData = event.payload.data
        const mediaEventsSelected = this.$utils.clone(this.mediaEventsSelected)
        const findMediaIndx = mediaEventsSelected.mediaSelection.findIndex((media) => { return media.resourceId === eventData.mediaResourceId })

        if (findMediaIndx >= 0) {
          const targetMedia = mediaEventsSelected.mediaSelection[findMediaIndx]
          const targetScheduleIndex = targetMedia.schedules.findIndex((schedule) => { return schedule.target === eventData.scheduleId })

          if (targetScheduleIndex >= 0) {
            const targetSchedule = targetMedia.schedules[targetScheduleIndex]
            const targetPlayTimeIndx = targetSchedule.custom.findIndex((playtime) => { return playtime.uid === eventData.playtimeUID })

            if (targetPlayTimeIndx >= 0 && targetSchedule.custom[targetPlayTimeIndx].adds) {
              // Repeat changed
              if (eventData.repeatChanged) {
                targetSchedule.custom[targetPlayTimeIndx].adds.repeat = eventData.repeat
              }

              // Sponsored text changed
              if (eventData.sponsoredChanged) {
                if ('sponsored' in targetSchedule.custom[targetPlayTimeIndx].adds && eventData.sponsored === undefined) {
                  delete targetSchedule.custom[targetPlayTimeIndx].adds.sponsored
                } else {
                  targetSchedule.custom[targetPlayTimeIndx].adds.sponsored = eventData.sponsored
                }
              }
            }

            this.mediaEventsSelected = mediaEventsSelected
          }
        }
      }
    },
    handlePlaytimeFormChange (event) {
      if (event.action === 'PLAYTIME_FORM_UPDATED') {
        const newPlaytime = event.payload.data.playtimeData
        if (!newPlaytime._edit) newPlaytime._edit = { title: '' }
        newPlaytime._edit.title = this.createScheduleTimeTitle(newPlaytime, { timeFormat: this.playtimeFormat }).range

        this.showPlaytimeFormSubmitBtn = true
        this.updatedPlaytimeFormData = newPlaytime
        this.playtimeFormData = newPlaytime
        this.$dataStore.setStore({ unsavedChangesInSchedulePagePlaytime: true })
        this.setUnsavedDataFlagIntoLocalstorage({ page: 'schedulePage' })
      }
    },
    handleSubmitPlaytimeForm (params = {}) {
      const updatedMediaEvents = this.$utils.clone(this.updatedMediaEvents)
      updatedMediaEvents.playtimeFormSubmitted = false
      updatedMediaEvents.updatedMedia = []

      this.$utils.mainLoader(true)

      for (const media of this.mediaEventsSelected.mediaSelection) {
        if (media.schedules) {
          const schedTargetIndx = media.schedules.findIndex((sched) => { return sched.target === this.mediaEventsSelected.scheduleId })
          const selectedPlaytimeRef = media._edit.selectedPlaytime

          for (const crntPlaytimeIndx in media.schedules[schedTargetIndx].custom) {
            const currentPlaytime = media.schedules[schedTargetIndx].custom[crntPlaytimeIndx]
            const isSelectedPlaytime = selectedPlaytimeRef.findIndex((selectedPlaytimeRef) => { return selectedPlaytimeRef.uid === currentPlaytime.uid }) >= 0

            // Keep the other original playtime property
            if (isSelectedPlaytime && this.updatedPlaytimeFormData) {
              const newPlaytime = this.$utils.clone(this.updatedPlaytimeFormData)
              newPlaytime.uid = currentPlaytime.uid
              newPlaytime.order = currentPlaytime.order
              newPlaytime.tag = currentPlaytime.tag
              newPlaytime.multi = currentPlaytime.multi
              newPlaytime.adds = currentPlaytime.adds

              if (currentPlaytime.excludes) {
                newPlaytime.excludes = currentPlaytime.excludes
              }

              media.schedules[schedTargetIndx].custom[crntPlaytimeIndx] = newPlaytime
            }
          }
        }

        if (media.resourceId) {
          updatedMediaEvents.updatedMedia.push(media)
        }
      }

      this.updatedMediaEvents = updatedMediaEvents

      const updateMediaDetails = () => {
        this.updatedMediaEvents.updatedMedia.filter((mediaItem, indx) => {
          mediaItem._edit.updateRequestStatus = 'requesting'

          if (mediaItem.resourceId && (!mediaItem._edit.updateRequestStatus || ['done'].indexOf(mediaItem._edit.updateRequestStatus) < 0)) {
            // Remove status and durtion
            if (mediaItem.status) {
              delete mediaItem.status
              delete mediaItem.dur
            }

            this.$api.put(`${CONFIG.API_BASE_URL}/media/${mediaItem.resourceId}?scheduleId=${this.mediaEventsSelected.scheduleId}`, mediaItem)
              .then(response => {
                // Update media request status to done
                this.updatedMediaEvents.updatedMedia.filter((media) => {
                  if (media.resourceId === response.data.resourceId) media._edit.updateRequestStatus = 'done'
                  return media
                })

                // If all request are done
                if (this.updatedMediaEvents.updatedMedia.every((mediaItem) => { return mediaItem._edit.updateRequestStatus === 'done' })) {
                  const updatedMediaEvents = this.$utils.clone(this.updatedMediaEvents)
                  updatedMediaEvents.selectedEventRowRef = this.mediaEventsSelected.eventRowSelection
                  updatedMediaEvents.playtimeFormSubmitted = true
                  this.updatedMediaEvents = updatedMediaEvents
                  this.showPlaytimeFormSubmitBtn = false

                  this.$dataStore.setStore({ unsavedChangesInSchedulePagePlaytime: false })
                  this.removeUnsavedDataFlagInLocalStorage()
                }

                if (params.nextAction) {
                  this.$nextTick(() => {
                    params.nextAction()
                  })
                }
              }).catch((error) => {
                this.$apiError.handleUnauthorizedError(error, { originalRequest: () => { updateMediaDetails() } })
              })
          }

          return mediaItem
        })
      }

      updateMediaDetails()
    },
    handleScheduleItemMouseEvent (mouseEvent, currentSchedule, domEvent) {
      this.mouseOverScheduleRow = (currentSchedule && currentSchedule._edit.uid) ? currentSchedule._edit.uid : null

      if (mouseEvent === 'mouseout') this.mouseOverScheduleRow = null
    },
    handleScheduleSearchBarChange (event) {
      if (event.action === 'NEW_PLAYER_SEARCH_RESULTS') {
        const scheduleList = this.$utils.clone(this.scheduleList)
        const newSearchResults = event.payload.data.searchResults

        scheduleList.itemsToShow = newSearchResults.fullDetailsList ? newSearchResults.fullDetailsList : []
        scheduleList.reqFilter = event.payload.data.reqSearchFilter
        scheduleList.nextFetch.hasMore = false
        scheduleList.nextFetch.nextToken = null

        // Cleanup player selection
        this.playerSelection = []
        this.scheduleList = this.filterScheduleList(scheduleList)

        // After backend request final result
        if (event.payload.data.afterAPIRequest) {
          this.isSearchLoading = false
          if (!this.scheduleList.itemsToShow.length) {
            this.showNoSearchResults = true
          } else {
            this.showNoSearchResults = false
          }
        } else {
          this.isSearchLoading = true
          if (this.scheduleList.itemsToShow.length) {
            this.showNoSearchResults = false
          }
        }

        // Set Tags
        if (event.payload.data.tags) {
          let newTags = this.playersTags.concat(event.payload.data.tags)
          newTags = newTags.filter((item, indx) => { return (newTags.indexOf(item) === indx) })
          this.playersTags = newTags
        }
      }

      if (event.action === 'RESET_TO_ORIGINAL_LISTS') {
        const scheduleList = this.$utils.clone(this.scheduleList)

        scheduleList.itemsToShow = []
        scheduleList.batchHistory.forEach((mediaByBatch) => {
          for (const indx in mediaByBatch) {
            const existInToShowList = scheduleList.itemsToShow.find((s) => { return s.scheduleId === mediaByBatch[indx].scheduleId })
            if (!existInToShowList) {
              scheduleList.itemsToShow.push(mediaByBatch[indx])
            }
          }
        })
        scheduleList.nextFetch.hasMore = false
        scheduleList.nextFetch.nextToken = null

        // Set Tags
        if (event.payload.data.tags) {
          let newTags = this.playersTags.concat(event.payload.data.tags)
          newTags = newTags.filter((item, indx) => { return (newTags.indexOf(item) === indx) })
          this.playersTags = newTags
        }

        this.scheduleList = this.filterScheduleList(scheduleList)
        this.isSearchLoading = false
        this.showNoSearchResults = false
      }

      if (event.action === 'SEARCH_REQUEST_FAILED') {
        this.isSearchLoading = false
      }

      if (event.action === 'NEW_PLAYER_SEARCH_TEXT') {
        this.playerSearchText = event.payload.data.text
      }

      if (event.action === 'NEW_PLAYER_SEARCH_TEXT_CONFIRM') {
        const playerSearchText = event.payload.data.text

        // Update current url
        const query = this.$route.query

        if (playerSearchText) {
          query.search = playerSearchText
        } else {
          delete query.search
        }

        this.$router.replace({ path: '/schedule' }).catch() // Redirect to main path first to avoid redundant navigation
        this.$router.replace({
          path: '/schedule',
          query
        }).catch()
      }
    },
    handleTagSliderChange (event) {
      if (event.action === CONST.EMIT_EVENT_ALL_TAG_SELECTED_FROM_SLIDER) {
        this.selectedTagsFromSlider = []
        const selectedDay = this.selectedDay

        this.createTimeOptionsAndGetSchedules({ event: 'allTagSelected', selectedDay })
        this.handleTimeSliderScroll()
        this.$dataStore.setStore({ unsavedChangesInSchedulePagePlaytime: false })
        this.$router.push({ path: '/schedule' })
      }

      if (event.action === CONST.EMIT_EVENT_NEW_SELECTED_TAGS_FROM_SLIDER) {
        this.$utils.mainLoader(true)
        this.selectedTagsFromSlider = event.payload.data

        if (!this.selectedTagsFromSlider.length) {
          this.$utils.mainLoader(false)
        }

        // Update current url
        const query = this.$route.query

        if (this.selectedTagsFromSlider.length) {
          query.tags = JSON.stringify(this.selectedTagsFromSlider)
        }

        this.$router.replace({ path: '/schedule' }).catch() // Redirect to main path first to avoid redundant navigation
        this.$router.push({
          path: '/schedule',
          query
        }).catch()
      }

      if (event.action === CONST.EMIT_EVENT_DONE_FETCHING_TAGS_FROM_SLIDER && event.payload.tagSliderComponentFirstLoad) {
        this.$utils.mainLoader(false)
      }
    },
    handleSelectedScheduleTagsSliderChange (event) {
      if (event.action === 'ALL_TAG_SELECTED') {
        this.handleShowingOfAllPlayers()
      }
    },
    setMaxPlayersPerRequest () {
      const schedulePlayerListContainerElHeight = $('.cpl-main').height()
      const defaultMaxRequest = 10
      const rowHeight = this.$utils.is1280View() ? 59 : 101
      const calcRows = (Math.round(schedulePlayerListContainerElHeight / rowHeight)) + 2
      this.playersMaxRequest = (calcRows > defaultMaxRequest) ? calcRows : defaultMaxRequest
    },
    handleTagExcludeIconClick (targetTag) {
      const mediaEventsSelected = this.$utils.clone(this.mediaEventsSelected)
      const mediaSelection = mediaEventsSelected.mediaSelection
      const getScheduleIndx = mediaSelection[0].schedules.findIndex((schedule) => { return schedule.target === this.selectedSchedule.scheduleId })

      if (getScheduleIndx >= 0) {
        // Target custom and only for adds items that are scheduled to a TAG or to smart-sync
        for (const customIndx in mediaSelection[0].schedules[getScheduleIndx].custom) {
          const customData = mediaSelection[0].schedules[getScheduleIndx].custom[customIndx]

          if ((customData.tag && customData.tag === `${targetTag}`) || (customData.tag === 'smart-tags' && ['smart sync', 'SMART SYNC'].indexOf(targetTag) >= 0)) {
            if (!customData.excludes) {
              customData.excludes = [this.selectedSchedule.scheduleId]
              this.showTagExcludeIcon = { show: true, icon: 'exclude' }
            } else if (customData.excludes && customData.excludes.indexOf(this.selectedSchedule.scheduleId) >= 0) {
              customData.excludes.splice(customData.excludes.indexOf(this.selectedSchedule.scheduleId), 1)
              this.showTagExcludeIcon = { show: true, icon: 'include' }
            } else {
              customData.excludes.push(this.selectedSchedule.scheduleId)
              this.showTagExcludeIcon = { show: true, icon: 'exclude' }
            }
          }
        }
      }

      this.mediaEventsSelected = mediaEventsSelected
      this.$dataStore.setStore({ unsavedChangesInSchedulePagePlaytime: true })
      this.showPlaytimeFormSubmitBtn = true
      this.tagExcludeIconChanged = true
    },
    handleSVGOnLoad ({ schedule }) {
      this.postMessageToSVG({ type: 'iframeOnLoad', schedule })
    },
    handleSVGMessage (event) {
      // Make sure event.origin is from the backend to prevent accepting message out of nowhere and event.data is an object
      const sessionStage = useCognitoAuthStore().sessionStage ?? CONFIG.GLOBAL_STAGE;
      const apiOrigin = (new URL(this.$utils.getStageAPIUrl(sessionStage))).origin
      if ((event.origin !== apiOrigin) || (!_.isPlainObject(event.data))) return

      // Update schedule status
      const errorStatus = ['unconfigured', 'failed', 'disconnected']

      if (this.scheduleList && this.scheduleList.itemsToShow) {
        for (const schedule of this.scheduleList.itemsToShow) {
          if (schedule.scheduleId === event.data.serialNumber) {
            schedule._isInErrorStatus = errorStatus.indexOf(event.data.status) >= 0
            schedule._playerStatus = event.data.status
          }
        }
      }
    },
    postMessageToSVG ({ type, schedule }) {
      const isElementVisible = (scheduleId) => {
        const schedulePreviewEl = $(`#scheduleListPreview-${scheduleId}`)

        if (!schedulePreviewEl.length) return false

        const schedulePreviewTop = $(schedulePreviewEl).offset().top
        const schedulePreviewBottom = schedulePreviewTop + $(schedulePreviewEl).height()
        const docViewTop = $('#schedulePlayerListContainer').scrollTop()
        const docViewBottom = docViewTop + $(window).height()

        return ((schedulePreviewBottom <= docViewBottom) && (schedulePreviewBottom >= docViewTop))
      }

      if ((type === 'iframeOnLoad' && schedule.scheduleId) && (isElementVisible(schedule.scheduleId))) {
        const msg = { activeSchedulePreview: schedule.scheduleId, visible: true }
        const schedulePreviewEl = $(`#scheduleListPreview-${schedule.scheduleId}`)
        schedulePreviewEl[0].contentWindow.postMessage(msg, '*')

        if (this.$currentUser.hasGroupDebug()) console.log('update snapshot ', schedule.name) // Debug
      } else if (type === 'selectedSchedule') {
        if (this.$currentUser.hasGroupDebug()) console.log('snapshot batch update start *******')

        for (const schedule of this.scheduleList.itemsToShow) {
          const schedulePreviewEl = $(`#scheduleListPreview-${schedule.scheduleId}`)
          const msg = { activeSchedulePreview: schedule.scheduleId, visible: false }

          if (schedulePreviewEl.length) schedulePreviewEl[0].contentWindow.postMessage(msg, '*')

          // Debug
          if (this.$currentUser.hasGroupDebug()) {
            if (msg.visible) {
              console.log('update snapshot ', schedule.name)
            }
          }
        }

        if (this.$currentUser.hasGroupDebug()) console.log('snapshot batch update end *******')
      } else if (type !== 'iframeOnLoad' && (this.scheduleList && this.scheduleList.itemsToShow)) {
        if (this.$currentUser.hasGroupDebug()) console.log('snapshot batch update start *******')

        for (const schedule of this.scheduleList.itemsToShow) {
          const schedulePreviewEl = $(`#scheduleListPreview-${schedule.scheduleId}`)
          const visible = isElementVisible(schedule.scheduleId)
          const msg = { activeSchedulePreview: schedule.scheduleId, visible: true }

          if (visible) {
            if (type === 'selectedSchedule' || type === 'documentHidden') msg.visible = false
            msg.visible = true
          } else if (!visible) {
            msg.visible = false
          }

          if (schedulePreviewEl.length) schedulePreviewEl[0].contentWindow.postMessage(msg, '*')

          // Debug
          if (this.$currentUser.hasGroupDebug()) {
            if (msg.visible) {
              console.log('update snapshot ', schedule.name)
            }
          }
        }

        if (this.$currentUser.hasGroupDebug()) console.log('snapshot batch update end *******')
      }
    },
    addMessageListener () {
      if (!this.handleSVGMessageEventListenerFunction) {
        this.handleSVGMessageEventListenerFunction = this.handleSVGMessage.bind(this)
        window.addEventListener('message', this.handleSVGMessageEventListenerFunction)
      }
    },
    removeMessageListener () {
      if (this.handleSVGMessageEventListenerFunction) {
        window.removeEventListener('message', this.handleSVGMessageEventListenerFunction)
        this.handleSVGMessageEventListenerFunction = null
      }
    },
    componentMountedActions () {
      this.addMessageListener()

      this.isAuthenticated({
        callback: () => {
          useMixpanel().track('Schedule')
          this.setMaxPlayersPerRequest()
          this.createTimeOptionsAndGetSchedules({ event: 'mounted' })
          this.handleTimeSliderScroll()
          this.$dataStore.setStore({ unsavedChangesInSchedulePagePlaytime: false })
        }
      })

      // Get Schedule list after window resize to make sure vertical pagination is not broken
      let resizeId = null
      $(window).on('resize', () => {
        clearTimeout(resizeId)
        resizeId = setTimeout(() => {
          this.setMaxPlayersPerRequest()
          this.createTimeOptionsAndGetSchedules({ event: 'windowScroll' })
          this.handleTimeSliderScroll()
        }, 500)
      })

      // Send a postMessage to all visible SVG templates to disable snapshots when the page/tab is not visible, and enable them again when it becomes visible
      this.cleanupVisibilityListenerCallback = this.$utils.handleDocumentVisibilityChange(
        () => this.postMessageToSVG({ type: 'documentHidden' }),
        () => {
          if (_.isEmpty(this.selectedSchedule)) {
            this.postMessageToSVG({ type: 'all' })
          }
        }
      )

      // Fetch schedules using infinite scroll
      this.$utils.infiniteScroll('schedulePlayerListContainer', () => {
        if (this.scheduleList.nextFetch.hasMore) {
          const getSchedStart = DateTime.fromISO(this.selectedDay.fullDate, { zone: 'utc' }).startOf('day').toISO()
          const getSchedDur = 24 * 3600 // 24H
          this.getScheduleList(
            getSchedStart,
            getSchedDur,
            {
              nextToken: this.scheduleList.nextFetch.nextToken
            })
        }
      })

      // Send postMessage on each player on scroll
      $('#schedulePlayerListContainer').on('scroll', () => {
        this.postMessageToSVG({ type: 'all' })
      })

      // Show schedule list on schedule menu clicked
      EventBus.$on('MENU_ITEM_CLICKED', (payload) => {
        if ((payload.routeName && payload.routeName === 'schedule') && this.selectedSchedule !== null) {
          this.handleShowingOfAllPlayers()
        }
      })

      // Show schedule list on selected schedule header clicked
      EventBus.$on('SELECTED_SCHEDULE_HEADER_CLICKED', (payload) => {
        this.handleShowingOfAllPlayers()
      })
    },
    /**
     * NOTE: We need to load Canvas to later understand is player configured for
     * Videowall or LED, so that pass respective Videowall or LED tag
     * to Create experience. User would see preview of Videowall
     * bezels and would be able to match players to publish
     * in Create experience.
     */
    getCanvasData () {
      const canvas = new Canvas(this.$api)
      this.$nextTick(async () => {
        const { ok: response, error } = await canvas.getCanvas()
        if (!error) {
          this.canvasRepository = response
        }
      })
    }
  },
  beforeUnmount () {
    this.removeMessageListener()

    if (_.isFunction(this.cleanupVisibilityListenerCallback)) {
      this.cleanupVisibilityListenerCallback()
    }
  },
  beforeRouteLeave (_to, from, next) {
    // Check first for unsaved data before redirecting anywhere
    this.checkUnsavedData({ nextAction: () => next() })
  },
  beforeRouteUpdate (_to, from, next) {
    // Check first for unsaved data before redirecting anywhere
    this.checkUnsavedData({ nextAction: () => next() })
  },
  mounted () {
    this.componentMountedActions()
    this.removeUnsavedDataFlagInLocalStorage()
    this.getCanvasData()

    addEventListener('popstate', (event) => {
      EventBus.$emit('WATCH_TAGS_SLIDER_SELECTION', {})

      if (this.selectedSchedule) {
        this.selectedSchedule = null
      } else {
        this.componentMountedActions()
      }
    })
  }
}
